import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useChatPanel } from '../../context/ChatPanelContext';
import { useLocale } from '../../i18n/useLocale';
import { CHEETAH_EXPERTS, type CheetahExpert } from './cheetahExperts';
import './CheetahPage.css';

const BADGE_PHOTO_URL = 'https://badgephotos.corp.amazon.com/';
const PHONETOOL_URL = 'https://phonetool.amazon.com/users/';

function ExternalIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden>
      <path d="M4 20H18.0195V24H2C0.89543 24 0 23.1046 0 22V6H4V20Z" fill="currentColor" />
      <path d="M22 0C23.1046 0 24 0.89543 24 2V14H20V6.83789L11.4121 15.4121L8.58789 12.582L17.1816 4H10V0H22Z" fill="currentColor" />
    </svg>
  );
}

function ExpertPhoto({ expert }: { expert: CheetahExpert }) {
  return (
    <div className="cheetah-expert__photo-wrap">
      <img
        className={`cheetah-expert__photo${expert.photoWideCrop ? ' cheetah-expert__photo--custom' : ''}`}
        src={expert.photo ?? `${BADGE_PHOTO_URL}?uid=${expert.alias}`}
        alt={expert.name}
        onError={(e) => {
          const img = e.target as HTMLImageElement;
          img.onerror = null;
          img.src = '/icons/user-avatar.svg';
        }}
      />
    </div>
  );
}

function ExpertCard({ expert, onOpen }: { expert: CheetahExpert; onOpen: () => void }) {
  const { t } = useTranslation('skills');
  const { locale } = useLocale();
  return (
    <article
      className="cheetah-expert"
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <ExpertPhoto expert={expert} />
      <div className="cheetah-expert__body">
        <div className="cheetah-expert__head">
          <h3 className="cheetah-expert__name">
            {expert.name} <span className="cheetah-expert__alias">({expert.alias}@)</span>
          </h3>
          <a
            className="cheetah-expert__link"
            href={`${PHONETOOL_URL}${expert.alias}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t('cheetah.phoneTool', { name: expert.name })}
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalIcon />
          </a>
        </div>
        <p className="cheetah-expert__title">{t('cheetah.expertTitle', { title: expert.title[locale] })}</p>
        <p className="cheetah-expert__bio">{expert.bio[locale]}</p>
        <ul className="cheetah-expert__tags">
          {expert.tags[locale].map((tag) => (
            <li key={tag} className="cheetah-expert__tag">
              {tag}
            </li>
          ))}
        </ul>
      </div>
    </article>
  );
}

function ExpertModal({ expert, onClose }: { expert: CheetahExpert; onClose: () => void }) {
  const { t } = useTranslation('skills');
  const { locale } = useLocale();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div className="cheetah-modal" role="dialog" aria-modal="true" aria-label={t('cheetah.modalLabel')} onClick={onClose}>
      <div className="cheetah-modal__panel" onClick={(e) => e.stopPropagation()}>
        <div className="cheetah-modal__header">
          <h2 className="cheetah-modal__title">{t('cheetah.modalTitle')}</h2>
          <button
            type="button"
            className="cheetah-modal__close"
            onClick={onClose}
            aria-label={t('cheetah.close')}
          >
            <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden>
              <path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="cheetah-modal__body">
          <div className="cheetah-modal__photo">
            <ExpertPhoto expert={expert} />
          </div>
          <div className="cheetah-modal__info">
            <div className="cheetah-expert__head">
              <h3 className="cheetah-expert__name">
                {expert.name} <span className="cheetah-expert__alias">({expert.alias}@)</span>
              </h3>
              <a
                className="cheetah-expert__link"
                href={`${PHONETOOL_URL}${expert.alias}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t('cheetah.phoneTool', { name: expert.name })}
              >
                <ExternalIcon />
              </a>
            </div>
            <p className="cheetah-expert__title">{t('cheetah.expertTitle', { title: expert.title[locale] })}</p>
            <p className="cheetah-modal__bio">{expert.bio[locale]}</p>
            <ul className="cheetah-expert__tags">
              {expert.tags[locale].map((tag) => (
                <li key={tag} className="cheetah-expert__tag">
                  {tag}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default function CheetahPage() {
  const { t } = useTranslation('skills');
  const navigate = useNavigate();
  const { sendPrompt } = useChatPanel();
  const [activeExpert, setActiveExpert] = useState<CheetahExpert | null>(null);

  return (
    <div className="cheetah-page">
      <button
        className="cheetah-page__back"
        onClick={() => navigate('/')}
        aria-label={t('cheetah.back')}
      >
        <svg
          className="cheetah-page__back-icon"
          viewBox="0 0 16 16"
          width="16"
          height="16"
          fill="none"
          aria-hidden
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M4.29289 8.70703L10.2929 14.707L11.7071 13.2928L6.41421 7.99993L11.7071 2.70703L10.2929 1.29282L4.29289 7.29282C3.90237 7.68334 3.90237 8.31651 4.29289 8.70703Z"
            fill="currentColor"
          />
        </svg>
        <span>Cheetah</span>
      </button>

      <section className="cheetah-banner" aria-label={t('cheetah.introLabel')}>
        <img className="cheetah-banner__art" src="/icons/cheetah-banner.png" alt="" />

        <div className="cheetah-banner__main">
          <div className="cheetah-banner__body">
            <h1 className="cheetah-banner__title">Cheetah</h1>
            <p className="cheetah-banner__desc">
              {t('cheetah.intro')}
            </p>
          </div>

          <div className="cheetah-banner__actions">
            <button
              type="button"
              className="cheetah-btn cheetah-btn--secondary"
              onClick={() => sendPrompt(t('cheetah.revisitPrompt'))}
            >
              {t('cheetah.revisit')}
            </button>
            <button
              type="button"
              className="cheetah-btn cheetah-btn--primary"
              onClick={() => sendPrompt(t('cheetah.bookPrompt'))}
            >
              {t('cheetah.book')}
            </button>
          </div>
        </div>
      </section>

      <section className="cheetah-experts" aria-label={t('cheetah.expertsLabel')}>
        {CHEETAH_EXPERTS.map((expert) => (
          <ExpertCard
            key={expert.alias}
            expert={expert}
            onOpen={() => setActiveExpert(expert)}
          />
        ))}
      </section>

      {activeExpert && (
        <ExpertModal expert={activeExpert} onClose={() => setActiveExpert(null)} />
      )}
    </div>
  );
}
